import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';

class LunarRefine extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('submit', this.handleSubmit, { signal });
    this.addEventListener('change', this.handleChange, { signal });
    this.addEventListener('keydown', this.handleKey, { signal });
    document.addEventListener('click', this.closeDropdowns, { signal });
    window.addEventListener('popstate', this.handleHistory, { signal });
    window.addEventListener('resize', this.handleResize, { signal });
    this.querySelector('dialog')?.addEventListener('close', this.resetSheet, { signal });
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    document.documentElement.classList.remove('lunar-refine-open');
  }

  resetSheet = () => {
    document.documentElement.classList.remove('lunar-refine-open');
    this.querySelector('[data-refine-sheet-form]')?.reset();
    this.refreshGroupCounts();
  };

  refreshGroupCounts = () => {
    for (const group of this.querySelectorAll('.lunar-refine__group')) {
      const count = group.querySelectorAll('input[type="checkbox"]:checked').length;
      const label = group.querySelector('[data-refine-group-count]');
      const text = count ? this.dataset.selectedLabel.replace('[count]', String(count)) : '';
      if (label) label.textContent = text;
      const summary = group.closest('.lunar-refine__accordion')?.querySelector('[data-refine-summary-count]');
      if (summary) summary.textContent = text;
    }
  };

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    const sheet = this.querySelector('dialog');
    if (target.closest('[data-refine-open]')) {
      sheet?.showModal();
      document.documentElement.classList.add('lunar-refine-open');
    }
    if (target.closest('[data-refine-close]') || target === sheet) sheet?.close();
    if (target.closest('[data-refine-reset]')) {
      const form = this.querySelector('[data-refine-sheet-form]');
      for (const input of form.querySelectorAll('input[type="checkbox"]')) input.checked = false;
      for (const input of form.querySelectorAll('input[type="number"]')) input.value = '';
      this.refreshGroupCounts();
    }
    if (target.closest('[data-refine-group-reset]')) {
      const details = target.closest('details');
      for (const input of details.querySelectorAll('input[type="checkbox"]')) input.checked = false;
      for (const input of details.querySelectorAll('input[type="number"]')) input.value = '';
    }
    const link = target.closest('a[data-refine-link]');
    if (link && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      this.navigate(new URL(link.href), link.id);
    }
    const summary = target.closest('.lunar-refine__dropdown > summary');
    if (summary) {
      for (const details of this.querySelectorAll('.lunar-refine__dropdown[open]')) {
        if (details !== summary.parentElement) details.open = false;
      }
    }
  };

  closeDropdowns = (event) => {
    if (!(event.target instanceof Element)) return;
    for (const details of this.querySelectorAll('.lunar-refine__dropdown[open]')) {
      if (!details.contains(event.target)) details.open = false;
    }
  };

  handleKey = (event) => {
    if (event.key !== 'Escape') return;
    const details = event.target.closest('.lunar-refine__dropdown[open]');
    if (details) {
      details.open = false;
      details.querySelector('summary')?.focus();
    }
  };

  handleChange = (event) => {
    if (event.target.closest('[data-refine-sheet-form]')) this.refreshGroupCounts();
    if (event.target.matches('[data-refine-sort]')) event.target.form.requestSubmit();
  };

  handleSubmit = (event) => {
    if (!event.target.matches('[data-refine-form]')) return;
    event.preventDefault();
    const form = event.target;
    const url = new URL(form.action);
    const params = new URLSearchParams(new FormData(form));
    for (const [key, value] of [...params]) if (value === '') params.delete(key);
    params.delete('page');
    url.search = params.toString();
    const focusId = form.hasAttribute('data-refine-sheet-form')
      ? this.querySelector('[data-refine-open]').id
      : form.querySelector('details[open] > summary')?.id || form.querySelector('[data-refine-sort]')?.id;
    this.querySelector('dialog[open]')?.close();
    for (const details of this.querySelectorAll('details[open]')) details.open = false;
    this.navigate(url, focusId);
  };

  handleHistory = () => this.navigate(new URL(window.location.href), null, false);

  handleResize = () => {
    if (this.getBoundingClientRect().width > 1099) this.querySelector('dialog[open]')?.close();
  };

  async navigate(url, focusId, push = true) {
    const navigation = Symbol();
    this.navigation = navigation;
    this.setAttribute('aria-busy', 'true');
    try {
      if (push) history.pushState({}, '', url);
      this.dispatchEvent(new FilterUpdateEvent(url.searchParams));
      await sectionRenderer.renderSection(this.dataset.sectionId, { url });
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    } catch {
      if (this.navigation === navigation) window.location.assign(url.href);
    } finally {
      if (this.navigation === navigation) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lunar-refine')) customElements.define('lunar-refine', LunarRefine);
